/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43fgc52/screen/56949300
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Col, Row } from 'react-bootstrap';
import { isEmpty, equals, isNil } from 'ramda';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Filter from '@components/@core/Filter';
import Input from '@components/@core/Input';
import Toggle from '@components/@core/Toggle';
import { QuotaType, QuotaSource, QuotaDirections } from '~/services/config';

class Add extends Component {
    state = {
        title: '',
        description: '',
        selected: {
            author: [],
            department: [],
            competency: [],
            category: [],
            types: [],
            source: [],
            track: false,
            direction: null,
        },
    };

    handleSelectTags = (key) => (tags) => {
        const { selected } = this.state;
        if (!equals(selected[key], tags)) {
            selected[key] = tags;
            this.setState({ selected });
        }
    };

    handleSubmit = (published) => () => {
        const { title, description, selected } = this.state;
        if (isEmpty(title) || isEmpty(selected.author) || isEmpty(selected.types) || isEmpty(selected.source)) {
            toast.error(`Title, Author, Types and Source are required.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        const data = {
            quota_template: {
                name: title,
                data: {
                    description,
                    source: selected.source[0].name,
                    target_types: selected.types[0].name,
                    track_daily: selected.track ? 'yes' : 'no',
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                },
                author_id: selected.author[0],
                quota_direction: isNil(selected.direction) ? QuotaDirections[0].value : selected.direction[0].value,
                published,
            },
        };
        const payload = { type: 'quotas', data };
        this.props.onCreate(payload);
    };

    render() {
        const { title, description, selected } = this.state;
        const { authors, departments, competencies, categories } = this.props;
        const disabled = !title || !description;

        return (
            <>
                <Filter back="all quotas" />
                <div className="lib-todo-quota" data-cy="quotaAddForm">
                    <div className="quota-detail">
                        <div className="d-flex py-2">
                            <p className="dsl-b18 bold mb-0">Add New Quota</p>
                        </div>
                        <Input
                            className="input-field"
                            dataCy="title"
                            title="Title"
                            type="text"
                            direction="vertical"
                            value={title}
                            placeholder="Type here..."
                            onChange={(e) => this.setState({ title: e })}
                        />
                        <Input
                            title="Description"
                            dataCy="description"
                            className="input-field input-textarea"
                            type="text"
                            value={description}
                            as="textarea"
                            rows="2"
                            direction="vertical"
                            placeholder="Type here..."
                            onChange={(e) => this.setState({ description: e })}
                        />
                        <Accordion className="settings-quota" icon="fas fa-gear" expanded>
                            <Row className="mt-1">
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field author"
                                        title="Author"
                                        direction="vertical"
                                        width="fit-content"
                                        dataCy="authors"
                                        data={authors}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('author')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Categories"
                                        direction="vertical"
                                        dataCy="categories"
                                        width="fit-content"
                                        data={categories}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('category')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Competencies"
                                        direction="vertical"
                                        dataCy="competencies"
                                        width="fit-content"
                                        data={competencies}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('competency')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field department"
                                        title="Departments"
                                        direction="vertical"
                                        dataCy="departments"
                                        width="fit-content"
                                        data={departments}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('department')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field"
                                        title="Direction"
                                        direction="vertical"
                                        width="fit-content"
                                        data={QuotaDirections}
                                        getValue={(e) => e['name']}
                                        dataCy="quotaDirections"
                                        returnBy="data"
                                        onChange={this.handleSelectTags('direction')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field"
                                        title="Source"
                                        direction="vertical"
                                        dataCy="quotaSource"
                                        width="fit-content"
                                        data={QuotaSource}
                                        getValue={(e) => e.name}
                                        returnBy="data"
                                        onChange={this.handleSelectTags('source')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field"
                                        title="Type"
                                        direction="vertical"
                                        dataCy="quotaType"
                                        width="fit-content"
                                        data={QuotaType}
                                        getValue={(e) => e.name}
                                        returnBy="data"
                                        onChange={this.handleSelectTags('types')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Toggle
                                        className="toggle-button"
                                        checked={selected.track}
                                        direction="vertical"
                                        title="Track Daily"
                                        leftLabel="Off"
                                        rightLabel="On"
                                        dataCy="trackDaily"
                                        onChange={this.handleSelectTags('track')}
                                    />
                                </Col>
                            </Row>
                        </Accordion>
                        <div className="mt-4 d-flex jc-end">
                            <Button className="mr-3" name="Save Draft" disabled={disabled} onClick={this.handleSubmit(0)} />
                            <Button name="Save & Publish" disabled={disabled} onClick={this.handleSubmit(1)} />
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

Add.propTypes = {
    authors: PropTypes.array.isRequired,
    departments: PropTypes.array.isRequired,
    competencies: PropTypes.array.isRequired,
    categories: PropTypes.array.isRequired,
    history: PropTypes.any.isRequired,
    onCreate: PropTypes.func.isRequired,
};

Add.defaultProps = {
    authors: [],
    departments: [],
    competencies: [],
    categories: [],
    history: [],
    onCreate: () => {},
};

export default Add;
